import { useMemo, useSyncExternalStore } from 'react';

/**
 * "The last session this browser saw was a member's" — a PAINTING hint for the
 * left menu, never an authorization decision.
 *
 * Owner, 2026-10-21: «при входе меню и Pro прорисовываются с задержкой». On
 * every hard load `AuthContext` starts at `status: 'loading'` and only flips to
 * `'authed'` once `GET /auth/me` answers, so the member-only «Моё» group in the
 * sidebar used to appear late and push the whole menu down. This remembers just
 * enough to draw that group straight away: whether the reader is paid (Команда /
 * Подписка rows) and whether they get the «Мои тендеры» row.
 *
 * It decides nothing. Every query, every route guard and every server check
 * still waits for the real session; the hint is dropped the moment `/auth/me`
 * answers. Its one known lie is harmless: after a logout on ANOTHER device, this
 * browser draws the old menu for a single load, until `/auth/me` returns 401 and
 * `refresh()` clears it.
 *
 * Written by `AuthProvider.refresh()` (paid, on every session read) and by the
 * sidebar (myTenders, once the saved-search list has resolved); cleared on a
 * clean 401 and on logout.
 */
export interface AuthHint {
  paid: boolean;
  myTenders: boolean;
}

const KEY = 'tenders.auth-hint';

/** Raw stored string — the `useSyncExternalStore` snapshot. A string compares by
 *  value, so React sees the same snapshot until the stored value changes; a
 *  freshly parsed object on every call would never compare equal. */
function readRaw(): string | null {
  // SSR / prerender: no hint, exactly as before this module existed.
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(KEY);
  } catch {
    // Storage disabled (private mode, blocked site data) — no hint.
    return null;
  }
}

function parse(raw: string | null): AuthHint | null {
  if (!raw) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
    const { paid, myTenders } = value as Partial<Record<keyof AuthHint, unknown>>;
    return { paid: paid === true, myTenders: myTenders === true };
  } catch {
    return null;
  }
}

export function readAuthHint(): AuthHint | null {
  return parse(readRaw());
}

export function writeAuthHint(hint: AuthHint): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(KEY, JSON.stringify({ paid: hint.paid, myTenders: hint.myTenders }));
  } catch {
    // Storage full/unavailable — the menu simply waits for the session, as before.
  }
}

export function clearAuthHint(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Nothing stored, or storage unavailable — either way there is no hint.
  }
}

// Nothing needs notifying: every write happens alongside an auth or query
// change that already re-renders the reader, and each render re-reads the
// snapshot. Same idiom as `use-hydrated.ts`.
const subscribe = () => () => {};
const none = (): string | null => null;

/**
 * The hint, or `null` while `active` is false.
 *
 * ⚠️ Not a plain `readAuthHint()` during render. The shell is prerendered and
 * `hydrateRoot`ed, and the server has no hint: drawing «Моё» from storage in the
 * FIRST client render would differ from the prerendered HTML — a React #418
 * hydration mismatch that discards and rebuilds the page on the client (see
 * `use-hydrated.ts` and `list-density.ts`, both measured). With a server
 * snapshot of `null`, a hydrated page matches the server and applies the hint
 * one re-render after hydration — still long before `/auth/me` answers — while
 * a client-only mount (`createRoot`) reads it on its very first render.
 */
export function useAuthHint(active: boolean): AuthHint | null {
  const raw = useSyncExternalStore(subscribe, active ? readRaw : none, none);
  return useMemo(() => parse(raw), [raw]);
}
